import styled, { css } from 'styled-components';
import {
  DESKTOP_MIN_BREAK,
  CHECKBOX_RADIO_HEIGHT_WIDTH,
} from '#/shared/constants';
import { screenReaderOnlyTextStyles } from '#/shared/components/Accessibility';
import SvgMap from '#/shared/components/GeneratedIcons/trimet/Map';
import EScooterIconBase from '#/shared/components/GeneratedIcons/trimet/Escooter';
import BicycleIconBase from '#/shared/components/GeneratedIcons/trimet/Bicycle';
import CarIconBase from '#/shared/components/GeneratedIcons/trimet/Car';
import ParkRideButtonBase from '#/shared/components/GeneratedIcons/trimet/buttons/Parking';
import StreetcarButtonBase from '#/shared/components/GeneratedIcons/trimet/buttons/Streetcar';
import TriMetButtonBase from '#/shared/components/GeneratedIcons/trimet/buttons/Trimet';
import MapControlsBase from '#/shared/components/GeneratedIcons/trimet/Mobile';
import TransitTrackerButtonBase from '#/shared/components/GeneratedIcons/trimet/buttons/TransitTracker';
import RentalCompanyIconBase from '#/shared/components/RentalCompanyIcon';
import StyledCheckbox from '#/shared/components/StyledCheckbox';
import RadioGroup from '#/shared/components/RadioGroup';
import {
  InputDiv,
  Label,
} from '#/shared/components/BaseCheckboxOrRadio/styled';
import { RadioGroupWrapper } from '#/shared/components/RadioGroup/styled';

export const ControlsInner = styled.div`
  max-width: 1500px;
  width: 100%;

  @media (min-width: ${DESKTOP_MIN_BREAK}) {
    padding-top: 50px;
  }
`;

export const ControlsBody = styled.div`
  display: flex;
  flex-direction: column;
  margin-bottom: 5px;
  padding: 0 10px;
`;

export const ControlsSection = styled.fieldset`
  margin-bottom: 10px;
  /* removes default border from fieldset */
  border: none;

  @media (min-width: ${DESKTOP_MIN_BREAK}) {
    display: flex;
    max-width: ${DESKTOP_MIN_BREAK};
    margin-bottom: 40px;
    position: relative;
    left: 50%;
    transform: translateX(-50%);
  }
`;

/*
 * this CSS intentionally duplicated from ScreenReaderText in order to not break fieldset/legend relationship
 * see https://adrianroselli.com/2022/07/use-legend-and-fieldset.html
 */
export const Legend = styled.legend`
  ${screenReaderOnlyTextStyles}
`;

export const SectionHeader = styled.div`
  display: flex;
  align-items: center;
  font-size: 1.1em;
  margin-top: 4px;
  padding: 3px 10px;
  color: ${props => props.theme.dialogText};

  @media (min-width: ${DESKTOP_MIN_BREAK}) {
    flex: 1 1 25%;
    align-items: flex-start;
  }
`;

export const SectionHeaderIcon = styled.div`
  flex: 0 0 32px;
  display: flex;
  align-content: center;
  align-items: center;
  min-height: 32px;
  min-width: 32px;
`;

export const SectionHeaderName = styled.div`
  flex: 1;
  display: flex;
  align-items: center;
  min-height: 32px;
  margin-left: 10px;
  color: ${props => props.theme.dialogText};

  @media (min-width: ${DESKTOP_MIN_BREAK}) {
    margin-left: 15px;
  }
`;

export const GridSection = styled.div`
  display: flex;
  flex: 0 0 auto;
  flex-flow: row wrap;
  margin: 0 -5px;
  @media (min-width: ${DESKTOP_MIN_BREAK}) {
    flex: 1 1 75%;
  }
`;

export const MapIcon = styled(SvgMap).attrs(props => ({
  width: 24,
  height: 24,
  title: 'Map Layers',
  fill: props.theme.dialogText,
}))`
  padding-left: 4px;
`;

export const BicycleIcon = styled(BicycleIconBase).attrs(props => ({
  width: 36,
  height: 36,
  title: 'Bicycle Rental Layers',
  fill: props.theme.dialogText,
}))`
  margin-top: -5px;
`;

export const ScooterIcon = styled(EScooterIconBase).attrs(props => ({
  width: 36,
  height: 36,
  title: 'E-Scooter Layers',
  fill: props.theme.dialogText,
}))`
  margin-top: -5px;
`;

export const CarIcon = styled(CarIconBase).attrs(props => ({
  width: 30,
  height: 30,
  title: 'Car Rental Layers',
  fill: props.theme.dialogText,
}))`
  margin-top: -2px;
`;

export const LayerToggleCollection = styled.div`
  display: flex;
  margin: 0 -5px;
  @media (min-width: ${DESKTOP_MIN_BREAK}) {
    flex: 1 1 75%;
  }
`;

const iconSize = 22;

export const RentalCompanyIcon = styled(RentalCompanyIconBase).attrs(props => ({
  width: iconSize,
  height: iconSize,
  title: props.title,
  fill: props.theme.dialogText,
}))``;

export const ParkRideButtonIcon = styled(ParkRideButtonBase).attrs(props => ({
  width: iconSize,
  height: iconSize,
  title: props.title,
  fill: props.theme.parkRideButtonBg,
}))``;

export const StreetcarButtonIcon = styled(StreetcarButtonBase).attrs(props => ({
  width: iconSize,
  height: iconSize,
  title: props.title,
}))``;

export const TriMetButtonIcon = styled(TriMetButtonBase).attrs(props => ({
  width: iconSize,
  height: iconSize,
  title: props.title,
}))``;

export const MapControlsIcon = styled(MapControlsBase).attrs(props => ({
  width: iconSize,
  height: iconSize,
  title: props.title,
  fill: props.theme.dialogText,
}))``;

export const TransitTrackerButtonIcon = styled(TransitTrackerButtonBase).attrs(
  props => ({
    width: iconSize,
    height: iconSize,
    title: props.title,
  })
)``;

const checkboxPadding = '12px';
const borderWidth = '1px';

const checkboxRadioSharedCss = css`
  ${Label} {
    padding: ${checkboxPadding};
    padding-right: calc(${CHECKBOX_RADIO_HEIGHT_WIDTH} + ${checkboxPadding});
    width: 100%;
    margin: 0;
  }
  ${InputDiv} {
    position: absolute;
    right: ${checkboxPadding};
  }
`;

const containerSharedCss = css`
  width: calc(50% - ${checkboxPadding} - ${borderWidth} * 2);
  @media (min-width: ${DESKTOP_MIN_BREAK}) {
    width: calc(33% - ${checkboxPadding} - ${borderWidth} * 2);
  }

  margin: 5px;
`;

const hoverStyle = css`
  @media (any-hover: hover) {
    &:hover {
      background-color: ${props => props.theme.toggleOptionBgHover};
    }
  }
`;

export const MapSettingsCheckbox = styled(StyledCheckbox)`
  width: 100%;
  ${checkboxRadioSharedCss}
  ${Label} {
    padding-left: calc(${checkboxPadding} + ${iconSize}px + 6px);
  }
`;

export const MapSettingsRadioGroup = styled(RadioGroup)`
  ${checkboxRadioSharedCss}
  ${containerSharedCss}

  ${RadioGroupWrapper} {
    > * {
      margin-bottom: 0;
      border: ${borderWidth} solid ${props => props.theme.toggleOptionBorder};

      ${hoverStyle}

      &:first-child {
        border-radius: ${props => props.theme.topRadius};
      }
      &:last-child {
        border-radius: ${props => props.theme.bottomRadius};
      }
      &:not(:last-child) {
        border-bottom: 0;
      }
    }
  }
`;

export const CheckboxWrapper = styled.div`
  ${containerSharedCss}

  border: ${borderWidth} solid ${props => props.theme.toggleOptionBorder};
  border-radius: ${props => props.theme.buttonRadius};
  display: flex;
  align-items: center;
  position: relative;

  ${hoverStyle}

  svg:first-child {
    position: absolute;
    pointer-events: none;
    left: ${checkboxPadding};
  }
`;
